import { useEffect, useState } from 'react';
import { products as productsApi } from '../lib/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';

/**
 * Stock-market style price chart for a product.
 * Props: productId (required), height (default 160), compact (boolean)
 */
export default function PriceChart({ productId, height = 160, compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    productsApi.priceHistory(productId, 90)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e?.message || 'Could not load history'); });
    return () => { cancelled = true; };
  }, [productId]);

  if (error) return <div className="text-xs text-ink/40 py-3">{error}</div>;
  if (!data) return <div className="text-xs text-ink/40 py-3">Loading price chart…</div>;

  const points = (data.points || []).map(p => ({
    ts: new Date(p.date).getTime(),
    label: new Date(p.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
    price: p.price,
  }));

  const stats = data.stats || {};
  const change = stats.change_pct || 0;
  const up = change > 0;
  const flat = Math.abs(change) < 0.5;
  const ChangeIcon = flat ? Minus : (up ? TrendingUp : TrendingDown);
  const changeCls = flat ? 'text-ink/50' : (up ? 'text-emerald-700' : 'text-red-600');
  const lineColor = flat ? '#94a3b8' : (up ? '#1B5E20' : '#dc2626');

  return (
    <div data-testid={`price-chart-${productId}`} className="card-soft p-3">
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-ink/60">
          <BarChart3 className="w-3 h-3"/> Price chart · 90d
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono font-bold text-ink">₹{stats.last?.toFixed(2)}</span>
          <span className={`inline-flex items-center gap-0.5 font-semibold ${changeCls}`}>
            <ChangeIcon className="w-3 h-3"/>{change > 0 ? '+' : ''}{change.toFixed(1)}%
          </span>
        </div>
      </div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <LineChart data={points} margin={{ top: 6, right: 6, bottom: compact ? 0 : 16, left: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} hide={compact} interval="preserveStartEnd"/>
            <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} domain={['dataMin - 5', 'dataMax + 5']} width={40} hide={compact}/>
            <Tooltip
              contentStyle={{ border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ fontWeight: 600 }}
              formatter={(v) => [`₹${v}`, 'Price']}
            />
            {!flat && <ReferenceLine y={stats.first} stroke="#cbd5e1" strokeDasharray="3 3" />}
            <Line type="monotone" dataKey="price" stroke={lineColor} strokeWidth={2.4} dot={{ r: 2.5, fill: lineColor }} activeDot={{ r: 5 }} isAnimationActive={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
      {!compact && (
        <div className="grid grid-cols-4 gap-2 mt-2 text-[10px] uppercase tracking-wider text-ink/50">
          <div><div className="text-[11px] font-bold text-ink normal-case tracking-normal">₹{stats.low?.toFixed(2)}</div>90d low</div>
          <div><div className="text-[11px] font-bold text-ink normal-case tracking-normal">₹{stats.high?.toFixed(2)}</div>90d high</div>
          <div><div className="text-[11px] font-bold text-ink normal-case tracking-normal">₹{stats.first?.toFixed(2)}</div>open</div>
          <div><div className={`text-[11px] font-bold normal-case tracking-normal ${changeCls}`}>{change > 0 ? '+' : ''}₹{stats.change_abs?.toFixed(2)}</div>change</div>
        </div>
      )}
    </div>
  );
}
